import React, { useState } from 'react';
import {
  Modal,
  Link as PdsLink,
  Form,
  Alert,
  Text,
} from '@puppet/react-components';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Cd4peError from '@components/Cd4peError';
import { useDeleteUser } from '@services/cd4pe/users';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import alerts from '@state/ui/alerts';
import { selectUser } from '@state/session/selectors';
import { UserV1 } from '@puppet/cd4pe-client-ts';
import { LINKS } from '../../../../routes';

interface DeleteUserModalProps {
  onClose: () => void;
  username: string;
}

type InputConfirmationForm = {
  confirmationInput: string;
};

const DeleteUserModal = ({ username, onClose }: DeleteUserModalProps) => {
  const [formValue, setFormValue] = useState<InputConfirmationForm>({
    confirmationInput: '',
  });
  const appDispatch = useAppDispatch();
  const { userName } = useAppSelector(selectUser) as UserV1;
  const deleteUser = useDeleteUser(userName!);

  const { t } = useTranslation('codeDelivery');

  const onSubmitHandler = () => {
    if (formValue.confirmationInput !== username) {
      return;
    }
    deleteUser.mutate(
      { usernameToDelete: username },
      {
        onSuccess: () => {
          appDispatch(
            alerts.actions.createAlert({
              type: 'Success',
              message: t('root.accounts.alert.deleteUser.success', {
                username,
              }),
            }),
          );
          onClose();
        },
      },
    );
  };

  const onChangeHandler = (
    nameChanged: keyof InputConfirmationForm,
    newValues: InputConfirmationForm,
  ) => {
    setFormValue(newValues);
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Title>{t('root.accounts.modal.deleteUser.title')}</Modal.Title>
      <div
        className="root_accounts_delete_user_modal"
        data-testid="delete-user-modal"
      >
        <Alert type="danger">
          {t('root.accounts.modal.deleteUser.form.alert')}
        </Alert>
        <Text>
          <Trans
            t={t}
            i18nKey="root.accounts.modal.deleteUser.paragraph.1"
            components={[<b />]}
            values={{ username }}
          />
        </Text>
        <Text>
          <Trans
            t={t}
            i18nKey="root.accounts.modal.deleteUser.paragraph.2"
            components={[
              <b />,
              <PdsLink as={Link} to={`${LINKS.cd4pe.root}/workspaces`} />,
            ]}
            values={{ username }}
          />
        </Text>
        <div className="form">
          <Form
            submittable
            cancellable
            submitType="danger"
            submitLabel={t('root.accounts.modal.deleteUser.form.label.submit')}
            cancelLabel={t('root.accounts.modal.deleteUser.form.label.cancel')}
            submitDisabled={formValue.confirmationInput !== username}
            onChange={onChangeHandler}
            onSubmit={onSubmitHandler}
            onCancel={onClose}
            submitting={deleteUser.isLoading}
          >
            <div className="form__input">
              <Form.Field
                type="text"
                label={t(
                  'root.accounts.modal.deleteUser.form.label.usernameInput',
                )}
                value={formValue}
                name="confirmationInput"
                data-testid="confirmation-input"
              />
            </div>
          </Form>
          <Cd4peError error={deleteUser.error} />
        </div>
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
